import { auth } from './firebase-config';

const API_BASE_URL = '/v1';

const getAuthToken = async (): Promise<string> => {
    const user = auth.currentUser;
    if (!user) {
        throw new Error('User not authenticated');
    }
    return await user.getIdToken();
};

export interface WalkingGroup {
    groupId: string;
    postcodePrefixes: string[];
    title: string;
    adminUserIds: string[];
    memberUserIds: string[];
    chatSessionId: string[];
    defaultLat?: number;
    defaultLong?: number;
}

export interface GetGroupsResponse {
    groups: WalkingGroup[];
}

export interface CreateGroupRequest {
    postcodePrefixes: string[];
    title: string;
    defaultLat?: number;
    defaultLong?: number;
}

export interface CreateGroupResponse {
    group: WalkingGroup;
}

export interface SetUserAdminRequest {
    userId: string;
    groupId: string;
}

export interface RemoveUserAdminRequest {
    userId: string;
    groupId: string;
}

export interface UpdateGroupTitleRequest {
    title: string;
}

export interface UpdateGroupRequest {
    title?: string;
    defaultLat?: number | null;
    defaultLong?: number | null;
}

export const getGroups = async (filters?: {
    postcodePrefix?: string;
    title?: string;
    missingAdmin?: boolean;
}): Promise<GetGroupsResponse> => {
    const token = await getAuthToken();
    const params = new URLSearchParams();
    if (filters?.postcodePrefix) params.append('postcodePrefix', filters.postcodePrefix);
    if (filters?.title) params.append('title', filters.title);
    if (filters?.missingAdmin) params.append('missingAdmin', 'true');

    const queryString = params.toString();
    const url = `${API_BASE_URL}/walking-groups-admin/groups${queryString ? `?${queryString}` : ''}`;

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to get groups');
    }

    const data = (await response.json())['data'];
    return data;
};

export const createGroup = async (request: CreateGroupRequest): Promise<CreateGroupResponse> => {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/walking-groups-admin/create-group`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(request)
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create group');
    }

    const data = (await response.json())['data'];
    return data;
};

export const removeGroup = async (groupId: string): Promise<void> => {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/walking-groups-admin/remove-group/${groupId}`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to remove group');
    }
};

export const setUserAdmin = async (request: SetUserAdminRequest): Promise<CreateGroupResponse> => {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/walking-groups-admin/set-user-admin`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(request)
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to set user as admin');
    }

    const data = (await response.json())['data'];
    return data;
};

export const removeUserAdmin = async (request: RemoveUserAdminRequest): Promise<CreateGroupResponse> => {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/walking-groups-admin/remove-user-admin`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(request)
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to remove user admin');
    }

    const data = (await response.json())['data'];
    return data;
};

export const updateGroupTitle = async (groupId: string, request: UpdateGroupTitleRequest): Promise<CreateGroupResponse> => {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/walking-groups-admin/update-group-title/${groupId}`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(request)
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update group title');
    }

    const data = (await response.json())['data'];
    return data;
};

export const updateGroup = async (groupId: string, request: UpdateGroupRequest): Promise<CreateGroupResponse> => {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/walking-groups-admin/update-group/${groupId}`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(request)
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update group');
    }

    const data = (await response.json())['data'];
    return data;
};

